import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../utils/apiFetch';
import { API_BASE } from '../utils/config';

interface LineItem {
  line_item_id: string;
  line_item: string;
  product_id: number | null;
  quantity: number;
  amount: number;
  gst_value: number;
  item_type: string;
  pair_id: number | null;
}

interface Order {
  bill_id: string;
  bill_number: number;
  billing_date: string;
  delivery_date: string;
  delivery_time?: string;
  total_amount: number;
  order_status: string;
  organization_name?: string;
  line_items: LineItem[];
  created_at: string;
  updated_at?: string;
}

interface TrackingEvent {
  label: string;
  description: string;
  date: string;
  time: string;
  completed: boolean;
  active: boolean;
}

function toIST(dateStr: string): { date: string; time: string } {
  const d = new Date(dateStr);
  return {
    date: d.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'Asia/Kolkata' }),
    time: d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' }),
  };
}

function buildTrackingEvents(order: Order): TrackingEvent[] {
  const status = order.order_status?.toLowerCase() ?? 'placed';
  const isConfirmed = status === 'confirmed' || status === 'delivered';
  const isDelivered = status === 'delivered';
  const placedIST = toIST(order.created_at ?? order.billing_date);
  const updatedIST = order.updated_at ? toIST(order.updated_at) : null;
  const deliveryIST = toIST(order.delivery_date);

  return [
    {
      label: 'Order Placed',
      description: 'Your order has been placed successfully.',
      date: placedIST.date,
      time: placedIST.time,
      completed: true,
      active: status === 'placed',
    },
    {
      label: 'Order Confirmed',
      description: 'Your order has been confirmed and is being prepared.',
      date: status === 'confirmed' && updatedIST ? updatedIST.date : '',
      time: status === 'confirmed' && updatedIST ? updatedIST.time : '—',
      completed: isConfirmed,
      active: status === 'confirmed',
    },
    {
      label: 'Delivered',
      description: 'Your order has been delivered.',
      date: status === 'delivered' && updatedIST ? updatedIST.date : deliveryIST.date,
      time: status === 'delivered' && updatedIST ? updatedIST.time : order.delivery_time || '—',
      completed: isDelivered,
      active: status === 'delivered',
    },
  ];
}

function VerticalTracker({ order }: { order: Order }) {
  const events = buildTrackingEvents(order);
  const lastCompletedIdx = events.reduce((acc, e, i) => (e.completed ? i : acc), -1);

  return (
    <div className="tracker">
      <div className="tracker__header">
        <div>
          <span className="tracker__label">Tracking ID</span>
          <span className="tracker__value">#{order.bill_number}</span>
        </div>
        <div style={{ textAlign: 'right' }}>
          <span className="tracker__label">Delivery Date</span>
          <span className="tracker__value">
            {new Date(order.delivery_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
          </span>
        </div>
      </div>

      <div className="tracker__timeline">
        {events.map((event, idx) => {
          const isLast = idx === events.length - 1;
          const dotDone = event.completed && !event.active;
          const dotActive = event.active;
          const lineDone = idx < lastCompletedIdx;

          return (
            <div key={idx} className="tracker__step">
              <div className="tracker__left">
                <div className={`tracker__dot ${dotActive ? 'tracker__dot--active' : dotDone ? 'tracker__dot--done' : ''}`} />
                {!isLast && <div className={`tracker__line ${lineDone ? 'tracker__line--done' : ''}`} />}
              </div>
              <div className={`tracker__content ${event.completed ? '' : 'tracker__content--dim'}`} style={{ paddingBottom: isLast ? 0 : 24 }}>
                {(idx === 0 || events[idx - 1].date !== event.date) && event.date && (
                  <span className="tracker__date">{event.date}</span>
                )}
                <div className="tracker__row">
                  <span className={`tracker__time ${event.active ? 'tracker__time--active' : ''}`}>{event.time}</span>
                  <div>
                    <p className={`tracker__event-label ${event.active ? 'tracker__event-label--active' : ''}`}>{event.label}</p>
                    <p className="tracker__event-desc">{event.description}</p>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function getStatusConfig(status: string) {
  switch (status?.toLowerCase()) {
    case 'confirmed': return { label: 'Order Confirmed', cls: 'order-status--confirmed' };
    case 'delivered': return { label: 'Delivered', cls: 'order-status--delivered' };
    default: return { label: 'Order Placed', cls: 'order-status--placed' };
  }
}

const OrdersPage: React.FC = () => {
  const { user, openLogin } = useAuth();
  const [orders, setOrders] = useState<Order[]>([]);
  const [filtered, setFiltered] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    apiFetch(`${API_BASE}/order`)
      .then(r => r.json())
      .then(data => {
        const raw: Order[] = (data.data || data.orders || []).map((o: any) => ({
          ...o,
          order_status: o.order_status || (o.is_delivered ? 'delivered' : 'placed'),
          line_items: o.line_items || [],
        }));
        setOrders(raw);
        setFiltered(raw);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user]);

  const applyFilters = (q: string, sf: string, base = orders) => {
    let result = base;
    if (q.trim()) {
      const lower = q.toLowerCase().replace(/^#/, '');
      result = result.filter(o =>
        String(o.bill_number).includes(lower) ||
        o.line_items.some(i => i.line_item?.toLowerCase().includes(lower))
      );
    }
    if (sf !== 'All') result = result.filter(o => o.order_status?.toLowerCase() === sf);
    setFiltered(result);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    applyFilters(e.target.value, statusFilter);
  };

  const handleStatusFilter = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(e.target.value);
    applyFilters(search, e.target.value);
  };

  if (!user) return (
    <div className="cart-page">
      <div className="empty-state">
        <div className="empty-state__icon">📦</div>
        <h2>Sign in to view your orders</h2>
        <button className="btn btn--primary" onClick={() => openLogin()}>Sign In</button>
      </div>
    </div>
  );

  if (loading) return (
    <div className="cart-page"><div className="skeleton" style={{ height: 200 }} /></div>
  );

  return (
    <div className="orders-page">
      <h1 className="orders-page__title">My Orders</h1>

      {/* Search & Filter */}
      <div className="orders-toolbar">
        <input
          className="orders-search"
          type="text"
          placeholder="Search by Bill Number or Item Name"
          value={search}
          onChange={handleSearch}
        />
        <select className="orders-filter" value={statusFilter} onChange={handleStatusFilter}>
          <option value="All">All Status</option>
          <option value="placed">Order Placed</option>
          <option value="confirmed">Order Confirmed</option>
          <option value="delivered">Delivered</option>
        </select>
      </div>

      {filtered.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state__icon">📦</div>
          <h2>No orders found</h2>
          <p>You haven't placed any orders yet.</p>
          <Link to="/products" className="btn btn--primary">Shop Now</Link>
        </div>
      ) : (
        <div className="orders-list">
          {filtered.map(order => {
            const cfg = getStatusConfig(order.order_status);
            return (
              <div key={order.bill_id} className="order-card">
                {/* Order Header */}
                <div className="order-card__header">
                  <div>
                    <p className="order-card__bill">Bill Number: #{order.bill_number}</p>
                    <p className="order-card__meta">Billed on: {new Date(order.billing_date).toLocaleDateString()}</p>
                    <p className="order-card__meta">
                      Delivery: {new Date(order.delivery_date).toLocaleDateString()}
                      {order.delivery_time && ` at ${order.delivery_time}`}
                    </p>
                    {order.organization_name && (
                      <p className="order-card__meta">Organization: {order.organization_name}</p>
                    )}
                  </div>
                  <div className="order-card__right">
                    <p className="order-card__amount-label">Total Amount:</p>
                    <p className="order-card__amount">₹{(order.total_amount ?? 0).toFixed(2)}</p>
                    <span className={`order-status ${cfg.cls}`}>{cfg.label}</span>
                  </div>
                </div>

                {/* Line Items — paired sets grouped, standalone items flat */}
                {(() => {
                  const pairMap = new Map<number, LineItem[]>();
                  const standalone: LineItem[] = [];
                  for (const item of order.line_items) {
                    if (item.pair_id != null) {
                      if (!pairMap.has(item.pair_id)) pairMap.set(item.pair_id, []);
                      pairMap.get(item.pair_id)!.push(item);
                    } else {
                      standalone.push(item);
                    }
                  }

                  const renderRow = (item: LineItem) => (
                    <div key={item.line_item_id} className="order-line-item">
                      <div className="order-line-item__col">
                        <span className="order-line-item__label">Item Name</span>
                        <span className="order-line-item__value">{item.line_item}</span>
                      </div>
                      <div className="order-line-item__col">
                        <span className="order-line-item__label">Qty</span>
                        <span className="order-line-item__value">{item.quantity}</span>
                      </div>
                      <div className="order-line-item__col">
                        <span className="order-line-item__label">Amount</span>
                        <span className="order-line-item__value">₹{(item.amount ?? 0).toFixed(2)}</span>
                      </div>
                      <div className="order-line-item__col">
                        <span className="order-line-item__label">Type</span>
                        <span className="order-line-item__value">{item.item_type}</span>
                      </div>
                    </div>
                  );

                  return (
                    <>
                      {standalone.map(renderRow)}
                      {Array.from(pairMap.entries())
                        .sort(([a], [b]) => a - b)
                        .map(([pairId, items]) => (
                          <div key={`pair-${pairId}`} className="order-pair-set">
                            <div className="order-pair-set__badge">Set {pairId}</div>
                            {items.map(renderRow)}
                          </div>
                        ))}
                    </>
                  );
                })()}

                {/* Track Order */}
                <div className="order-card__footer">
                  <button
                    className="btn btn--primary"
                    onClick={() => setSelectedOrder(order)}
                  >
                    Track Order
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Track Order Modal */}
      {selectedOrder && (
        <div className="addr-modal-overlay" onClick={() => setSelectedOrder(null)}>
          <div className="addr-modal tracker-modal" onClick={e => e.stopPropagation()}>
            <div className="tracker-modal__header">
              <div>
                <p style={{ fontWeight: 700, fontSize: 15, margin: 0 }}>Shipped with Store</p>
                <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>Bill #{selectedOrder.bill_number}</p>
              </div>
              <button className="addr-icon-btn" onClick={() => setSelectedOrder(null)} title="Close">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <hr style={{ margin: '12px 0', borderColor: '#e5e7eb' }} />
            <VerticalTracker order={selectedOrder} />
            <div className="addr-modal__actions" style={{ marginTop: 16 }}>
              <button className="btn btn--outline" onClick={() => setSelectedOrder(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrdersPage;
